import { useEffect, useState } from 'react';
import type { StatsOverview } from '@shared/types';
import { IDEA_STATUS_LABELS } from '@shared/ideaStatus';
import { formatRate, tagTone } from '@shared/format';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { Badge, QualityBar } from '../../components/Badge';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner, Stat } from '../../components/Card';
import { api, errorMessage } from '../../lib/api';
import { EventFeed } from '../ideas/components/EventFeed';

/** 数据看板。刻意不按人排名：这个平台不是绩效工具。 */
export function StatsPage() {
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<StatsOverview>('/api/ideas/stats')
      .then((result) => {
        if (!cancelled) setStats(result);
      })
      .catch((fetchError) => {
        if (!cancelled) setError(errorMessage(fetchError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Spinner /> 正在统计…
        </div>
      </PageContainer>
    );
  }

  if (!stats) {
    return (
      <PageContainer>
        <Card>
          <CardBody>
            <EmptyState title="暂时拿不到统计数据" description={error ?? '请稍后重试。'} />
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  const decided = stats.ai.goCount + stats.ai.needsMoreInfoCount + stats.ai.noGoCount;
  const maxStatus = Math.max(1, ...stats.byStatus.map((item) => item.count));

  return (
    <PageContainer>
      <PageHeader
        title="数据看板"
        description="创意总量、AI 研判的结论分布与最近动态。所有数字都来自平台自身的记录，不做人工估算。"
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardBody>
            <Stat label="创意总量" value={stats.total} hint={`提出人 ${stats.people.authors} 位`} />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat
              label="AI 通过率"
              value={formatRate(stats.ai.passRate)}
              hint={decided > 0 ? `${stats.ai.goCount} / ${decided} 份最新结论建议推进` : '还没有有效结论'}
              tone="ok"
            />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat label="平均综合分" value={stats.ai.avgScore ?? '—'} hint="按每个创意的最新一份结论计算" />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat label="研判次数" value={stats.ai.runs} hint={`成功 ${stats.ai.succeeded} · 失败 ${stats.ai.failed}`} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle subtitle="只显示你自己的累计用量，不与他人对比 —— 用量不是考核指标">
            我的 AI 用量
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="我的合计 token" value={stats.myUsage.totalTokens.toLocaleString('zh-CN')} hint="输入 + 输出" />
            <Stat
              label="我的请求次数"
              value={stats.myUsage.requests}
              hint={`成功 ${stats.myUsage.succeeded} · 失败 ${stats.myUsage.failed}`}
            />
            <Stat
              label="其中思考 token"
              value={stats.myUsage.reasoningTokens.toLocaleString('zh-CN')}
              hint="已含在输出 token 内"
            />
            <Stat
              label="未计量次数"
              value={Math.max(0, stats.myUsage.requests - stats.myUsage.meteredRequests)}
              tone={stats.myUsage.requests > stats.myUsage.meteredRequests ? 'caution' : 'neutral'}
              hint="未收到模型响应的调用，token 未知"
            />
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle subtitle="草稿与归档不影响推进节奏，单独统计">状态分布</CardTitle>
          </CardHeader>
          <CardBody>
            {stats.byStatus.length === 0 ? (
              <EmptyState title="还没有创意" />
            ) : (
              <ul className="flex flex-col gap-2.5">
                {stats.byStatus.map((item) => (
                  <li key={item.status} className="flex flex-col gap-1">
                    <div className="flex items-baseline justify-between text-[11px]">
                      <span className="text-fg-muted">{IDEA_STATUS_LABELS[item.status]}</span>
                      <span className="tabular text-fg">{item.count}</span>
                    </div>
                    <QualityBar value={item.count} max={maxStatus} tone="accent" ariaLabel={IDEA_STATUS_LABELS[item.status]} />
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle subtitle="按未归档创意统计">标签分布</CardTitle>
          </CardHeader>
          <CardBody>
            {stats.byTag.length === 0 ? (
              <EmptyState title="还没有标签" description="提交创意时选中分类标签，这里就会有分布。" />
            ) : (
              <ul className="flex flex-wrap gap-2">
                {stats.byTag.map((item) => (
                  <li key={item.tag} className="flex items-center gap-1.5">
                    <Badge tone={tagTone(item.tag)}>{item.tag}</Badge>
                    <span className="tabular text-[11px] text-fg-muted">{item.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle subtitle="提出、研判、流转、讨论都会记一条">最近动态</CardTitle>
        </CardHeader>
        <CardBody>
          <EventFeed events={stats.recentEvents} />
        </CardBody>
      </Card>

      <p className="text-[11px] leading-relaxed text-fg-subtle">
        通过率只统计「每个创意最新一份研判结论」中的建议推进比例，重复研判不会把分母灌水。
        平台不做人均产出与排名 —— 创意量不应该是考核指标。
        「我的 AI 用量」只看得到自己的数字；跨成员的用量明细在管理员专属的「资源用量」页，
        那里用于算账与看配额，同样不作为绩效评价依据。
      </p>
    </PageContainer>
  );
}
